"use client";

import React, { useMemo, useState } from "react";
import type { Swiper as SwiperClass } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Thumbs, Zoom } from "swiper/modules";
import {
  CheckCircle2,
  AlertCircle,
  Truck,
  RotateCcw,
  ShieldCheck,
  Flame,
  ArrowRight,
  HelpCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "@/components/common/Link";
import { decodeEntities } from "@/lib/decodeEntities";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  VariableProductAddToCart,
  ProductPrice,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import type { EcommerceProduct } from "@/integrations/wordpress/types";
import { RelatedProducts } from "./RelatedProducts";

import "swiper/css";
import "swiper/css/thumbs";
import "swiper/css/zoom";
import "swiper/css/a11y";

function ProductBreadcrumbs({ product }: { product: EcommerceProduct }) {
  const { links, loading } = useShopBreadcrumbs();

  if (!loading && links && links.length > 0) {
    const parentLinks = links.slice(0, -1);
    const lastLink = links[links.length - 1];

    return (
      <nav aria-label="Breadcrumb" className="mb-6">
        <Breadcrumb>
          <BreadcrumbList className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/" className="hover:text-primary transition-colors">
                  Home
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/shop" className="hover:text-primary transition-colors">
                  Shop
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {parentLinks.map((link) => {
              const title = decodeEntities(link.title);
              // Avoid repeating Home or Shop if already present
              if (
                title.toLowerCase() === "home" ||
                title.toLowerCase() === "shop" ||
                title.toLowerCase() === "catalog"
              ) {
                return null;
              }
              return (
                <React.Fragment key={link.href}>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to={link.href} className="hover:text-primary transition-colors">
                        {title}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </React.Fragment>
              );
            })}
            {lastLink && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-semibold text-foreground line-clamp-1 max-w-[240px] sm:max-w-md">
                    {decodeEntities(lastLink.title)}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </nav>
    );
  }

  // Fallback category breadcrumbs: Home / Shop / category / product
  const primaryCat = product.categories?.[0];
  const catSlug = primaryCat?.slug;
  const catLink = catSlug ? `/shop?category=${catSlug}` : "/shop";

  return (
    <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-muted-foreground">
      <Link to="/" className="hover:text-primary transition-colors">
        Home
      </Link>
      <span>/</span>
      <Link to="/shop" className="hover:text-primary transition-colors">
        Shop
      </Link>
      {primaryCat && (
        <>
          <span>/</span>
          <Link to={catLink} className="hover:text-primary transition-colors">
            {decodeEntities(primaryCat.name)}
          </Link>
        </>
      )}
      <span>/</span>
      <span className="text-foreground line-clamp-1 max-w-[200px] sm:max-w-sm">
        {decodeEntities(product.title)}
      </span>
    </nav>
  );
}

function SingleProductDetail() {
  const { posts, loading, error, refetch } = useEcommerceProducts();
  const product = posts?.[0];

  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);

  // Loading skeleton state
  if (loading) {
    return (
      <div className="space-y-12">
        <div className="h-4 w-48 bg-muted rounded animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-6 space-y-4">
            <Skeleton className="aspect-square w-full rounded-md" />
            <div className="grid grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="aspect-square rounded-md" />
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 space-y-6">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-10 w-4/5" />
            <Skeleton className="h-8 w-44" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  // Error state with retry
  if (error) {
    return (
      <div className="mx-auto max-w-xl text-center py-16 px-6 rounded-md border border-border bg-card shadow-xs">
        <div className="mx-auto size-12 rounded-md bg-muted flex items-center justify-center text-destructive mb-4 border border-border">
          <AlertCircle className="size-6" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground font-serif">
          Failed to Load Product
        </h2>
        <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
          {error || "An unexpected error occurred while retrieving equipment details."}
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <Button onClick={() => refetch()} variant="outline">
            Try Again
          </Button>
          <Button asChild variant="default">
            <Link to="/shop">Return to Shop</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Not found state
  if (!product) {
    return (
      <div className="mx-auto max-w-xl text-center py-16 px-6 rounded-md border border-border bg-card shadow-xs">
        <div className="mx-auto size-12 rounded-md bg-muted flex items-center justify-center text-primary mb-4 border border-border">
          <AlertCircle className="size-6" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground font-serif">
          Product Not Found
        </h1>
        <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
          The requested water heater model does not exist or has been discontinued.
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <Button asChild variant="default">
            <Link to="/shop">Return to Shop</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Build the list of gallery images
  const rawImages: string[] = [];
  if (activeImage) {
    rawImages.push(activeImage);
  }
  if (product.featured_image_url && !rawImages.includes(product.featured_image_url)) {
    rawImages.push(product.featured_image_url);
  }
  if (product.gallery_image_urls && product.gallery_image_urls.length > 0) {
    product.gallery_image_urls.forEach((imgUrl) => {
      if (imgUrl && !rawImages.includes(imgUrl)) {
        rawImages.push(imgUrl);
      }
    });
  }

  const inStock = product.is_in_stock !== false && product.stock_status !== "outofstock";
  const primaryCat = product.categories?.[0];

  // Check whether long_description exists and has actual textual content (not just empty html tags)
  const hasLongDescription = Boolean(
    product.long_description &&
      product.long_description.replace(/<[^>]*>/g, "").trim().length > 0
  );

  return (
    <>
      <ProductBreadcrumbs product={product} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        {/* Left Column: Image Gallery (single image clean display or Swiper thumbs/zoom) */}
        <div className="lg:col-span-6">
          <div className="overflow-hidden rounded-md border border-border bg-card p-4 shadow-xs">
            {rawImages.length === 0 ? (
              <div className="aspect-square flex flex-col items-center justify-center bg-muted/40 text-muted-foreground rounded-md">
                <Flame className="size-12 stroke-[1.5] mb-2 text-primary" />
                <span className="text-sm font-medium">Residential Gas Water Heater</span>
              </div>
            ) : rawImages.length === 1 ? (
              <div className="relative aspect-square w-full flex items-center justify-center p-6 bg-card rounded-md border border-border/60">
                <img
                  src={rawImages[0]}
                  alt={decodeEntities(product.title)}
                  className="max-h-full max-w-full object-contain mx-auto"
                  loading="eager"
                />
              </div>
            ) : (
              <div className="space-y-4">
                <Swiper
                  modules={[Thumbs, Zoom, A11y]}
                  zoom
                  thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                  a11y={{
                    prevSlideMessage: "Previous product view",
                    nextSlideMessage: "Next product view",
                  }}
                  className="rounded-md border border-border/60 overflow-hidden"
                >
                  {rawImages.map((imgUrl, i) => (
                    <SwiperSlide key={imgUrl || i} data-index={i} zoom>
                      <div className="relative aspect-square w-full flex items-center justify-center p-6 bg-card">
                        <img
                          src={imgUrl}
                          alt={decodeEntities(product.title)}
                          className="max-h-full max-w-full object-contain mx-auto"
                          loading="lazy"
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>

                {/* Thumbnail Strip */}
                <Swiper
                  modules={[Thumbs]}
                  onSwiper={setThumbsSwiper}
                  watchSlidesProgress
                  slidesPerView={4}
                  spaceBetween={10}
                  className="thumb-carousel"
                >
                  {rawImages.map((imgUrl, i) => (
                    <SwiperSlide
                      key={imgUrl || i}
                      data-index={i}
                      className="cursor-pointer"
                      onClick={() => setActiveImage(imgUrl)}
                    >
                      <div className="aspect-square rounded-md border border-border p-1 bg-card hover:border-primary transition-colors [.swiper-slide-thumb-active_&]:border-primary [.swiper-slide-thumb-active_&]:ring-2 [.swiper-slide-thumb-active_&]:ring-primary/20">
                        <img
                          src={imgUrl}
                          alt={decodeEntities(product.title)}
                          className="h-full w-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
          </div>

          {/* Reassurance list (compact strip) */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="flex items-center gap-2 p-3 rounded-md border border-border bg-card">
              <Truck className="size-4 text-primary shrink-0" />
              <span className="font-medium text-foreground">Ships across the United States</span>
            </div>
            <Link
              to="/ReturnsPage"
              className="flex items-center gap-2 p-3 rounded-md border border-border bg-card hover:border-primary transition-colors"
            >
              <RotateCcw className="size-4 text-primary shrink-0" />
              <span className="font-medium text-foreground">14-day returns (unused, unopened, not installed)</span>
            </Link>
            <div className="flex items-center gap-2 p-3 rounded-md border border-border bg-card">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span className="font-medium text-foreground">Secure checkout</span>
            </div>
          </div>
        </div>

        {/* Right Column: Title, Price, Summary, Cart, Details */}
        <div className="lg:col-span-6 flex flex-col gap-6">
          {/* Header Info */}
          <div className="border-b border-border pb-5">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge
                variant="outline"
                className="bg-muted text-foreground border-border text-[11px] font-mono"
              >
                SKU: {product.slug || `AQ-${product.id}`}
              </Badge>
              {primaryCat && (
                <Badge variant="secondary" className="text-[11px]">
                  {decodeEntities(primaryCat.name)}
                </Badge>
              )}
              {product.on_sale && (
                <Badge variant="destructive" className="text-[11px]">
                  Sale
                </Badge>
              )}
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
              {decodeEntities(product.title)}
            </h1>

            {/* Stock status indicator */}
            <div className="mt-3 flex items-center gap-2">
              {inStock ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground bg-muted px-2.5 py-1 rounded-md border border-border">
                  <CheckCircle2 className="size-3.5 text-primary" />
                  In Stock
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-destructive bg-destructive/10 px-2.5 py-1 rounded-md border border-destructive/20">
                  <AlertCircle className="size-3.5" />
                  Out of Stock
                </span>
              )}
            </div>
          </div>

          {/* Price (Sale price handled by ProductPrice) */}
          <div className="text-3xl sm:text-4xl font-bold text-secondary font-mono tracking-tight">
            <ProductPrice product={product} />
          </div>

          {/* Short overview summary */}
          {product.short_description && (
            <div
              className="text-sm leading-relaxed text-muted-foreground font-sans prose prose-sm max-w-none [&_p]:m-0"
              dangerouslySetInnerHTML={{
                __html: decodeEntities(product.short_description),
              }}
            />
          )}

          {/* Add to Cart Area */}
          <div className="border border-border rounded-md bg-card p-5 space-y-4 shadow-xs">
            <VariableProductAddToCart
              product={product}
              showQuantity={true}
              variant="default"
              size="lg"
              className="w-full"
              buttonClassName="w-full font-semibold h-11 shadow-xs bg-primary text-primary-foreground hover:bg-primary/90"
              onVariationResolved={(variation) => {
                if (variation?.featured_image_url) {
                  setActiveImage(variation.featured_image_url);
                }
              }}
            />

            {/* Support info note */}
            <div className="pt-2 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <HelpCircle className="size-3.5 text-primary" />
                Questions about this model?
              </span>
              <Link
                to="/ContactPage"
                className="font-semibold text-foreground hover:text-primary transition-colors"
              >
                carry6891@outlook.com
              </Link>
            </div>
          </div>

          {/* Attributes / specifications only if present in product */}
          {product.attributes && product.attributes.length > 0 && (
            <div className="rounded-md border border-border bg-card p-5">
              <h2 className="text-xs font-bold uppercase tracking-wider text-foreground mb-3">
                Product Specifications
              </h2>
              <dl className="divide-y divide-border/60 text-xs">
                <div className="py-2 flex justify-between">
                  <dt className="text-muted-foreground">Fuel Type</dt>
                  <dd className="font-semibold text-foreground">Natural Gas</dd>
                </div>
                {product.attributes.map((attr) => (
                  <div key={attr.name} className="py-2 flex justify-between">
                    <dt className="text-muted-foreground">{decodeEntities(attr.name)}</dt>
                    <dd className="font-semibold text-foreground">
                      {attr.terms.map((t) => decodeEntities(t.name)).join(", ")}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* Long Description: rendered ONLY if it has content */}
          {hasLongDescription && (
            <div className="border-t border-border pt-6">
              <h2 className="text-base font-bold text-foreground mb-3 font-serif">
                Product Details
              </h2>
              <div
                className="prose prose-sm text-muted-foreground leading-relaxed font-sans max-w-none"
                dangerouslySetInnerHTML={{ __html: product.long_description || "" }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Related Products row in same category */}
      <RelatedProducts product={product} />
    </>
  );
}

export default function SingleProduct() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient?.getCurrentWPQueryParams
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "product" };

  const wp_query = useMemo(() => {
    return new WP_Query({
      ...currentWPQueryParams,
      post_type: "product",
      per_page: 1,
    });
  }, [JSON.stringify(currentWPQueryParams)]);

  return (
    <section
      id="product-detail"
      data-nav="light"
      className="bg-background text-foreground py-12 md:py-20 border-b border-border"
    >
      <div className="mx-auto max-w-[1240px] px-4 md:px-6">
        <ShopBreadcrumbsProvider>
          <EcommerceProductsProvider wp_query={wp_query}>
            <SingleProductDetail />
          </EcommerceProductsProvider>
        </ShopBreadcrumbsProvider>
      </div>
    </section>
  );
}
